import React from 'react';
import { LogOut, User, Menu, Clock, MapPin, ChevronDown } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { useActiveShift } from '@/hooks/useActiveShift';
import { NotificationDropdown } from '@/components/notifications/NotificationDropdown';
import { HelpButton } from '@/components/HelpButton';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

interface HeaderProps {
  onMenuClick?: () => void;
}

export const Header: React.FC<HeaderProps> = ({ onMenuClick }) => {
  const { user, logout, selectedLocation } = useAuth();
  const { shiftName, startTime, endTime, isValidShift, isLocationOpen } = useActiveShift();

  const handleLogout = async () => {
    await logout();
  };

  return (
    <header className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
      <div className="flex items-center space-x-4">
        {onMenuClick && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onMenuClick}
            className="md:hidden"
          >
            <Menu className="h-5 w-5" />
          </Button>
        )}

        <h1 className="text-lg font-semibold text-gray-900">
          Macartur POS
        </h1>
      </div>

      <div className="flex items-center space-x-4">
        {/* Información del local - Solo para CAJEROS (positionId = 3) */}
        {user?.positionId === 3 && selectedLocation && (
          <div className="hidden md:flex items-center space-x-2 px-3 py-1.5 rounded-lg border border-blue-200 bg-blue-50">
            <MapPin className="h-4 w-4 text-blue-600" />
            <span className="text-sm font-medium text-blue-800">
              {selectedLocation.name}
            </span>
          </div>
        )}

        {/* Información del turno activo - Solo para CAJEROS (positionId = 3) */}
        {user?.positionId === 3 && (
          <div className={`hidden md:flex items-center space-x-2 px-3 py-1.5 rounded-lg border ${
            isLocationOpen
              ? 'border-green-200 bg-green-50'
              : 'border-red-200 bg-red-50'
          }`}>
            <Clock className={`h-4 w-4 ${
              isLocationOpen ? 'text-green-600' : 'text-red-600'
            }`} />
            <span className={`text-sm font-medium ${
              isLocationOpen ? 'text-green-800' : 'text-red-800'
            }`}>
              {shiftName} ({startTime} - {endTime})
            </span>
          </div>
        )}
        
        {/* Ayuda contextual */}
        <HelpButton />

        {/* Notificaciones del sistema */}
        <NotificationDropdown />
        
        {user && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="flex items-center space-x-2">
                <User className="h-4 w-4" />
                <span className="hidden md:inline text-sm">
                  {user.nombre} {user.apellidos}
                </span>
                <ChevronDown className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <div className="flex flex-col space-y-1">
                  <p className="text-sm font-medium">{user.nombre} {user.apellidos}</p>
                  <p className="text-xs text-muted-foreground">{user.email}</p>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                <LogOut className="mr-2 h-4 w-4" />
                <span>Cerrar Sesión</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </header>
  );
};